// ── Section-snap scroll driver ────────────────────────────────────────────────
// One scroll/swipe → advance one snap point. Locked while tween is running.

export function buildScrollDriver(isMobile = false) {
  let snaps   = []
  let current = 0

  let tweenFrom    = 0
  let tweenTo      = 0
  let tweenStart   = -1
  let tweening     = false
  let lastTweenEnd = -Infinity   // timestamp when last tween finished
  const TWEEN_MS_BASE = 750
  const COOLDOWN      = isMobile ? 250 : 700

  let smooth = 0

  function easeInOut(t) {
    return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t
  }

  let tweenDuration = TWEEN_MS_BASE

  function goToIndex(idx, ms = TWEEN_MS_BASE) {
    idx = Math.max(0, Math.min(snaps.length - 1, idx))
    if (idx === current && tweening) return
    tweenDuration = ms
    current    = idx
    tweenFrom  = smooth
    tweenTo    = snaps[idx]
    tweenStart = performance.now()
    tweening   = true
  }

  // ── Input handlers ───────────────────────────────────────────────────────

  let onHorizontalDelta = null
  function setHorizontalHandler(cb) { onHorizontalDelta = cb }

  function blocked() {
    const now = performance.now()
    // Desktop: lock during tween + cooldown (original behaviour)
    // Mobile: only lock during cooldown — allows mid-tween redirect
    return (isMobile ? false : tweening) || (now - lastTweenEnd < COOLDOWN)
  }

  window.addEventListener('wheel', (e) => {
    // Gallery overlay open — let it scroll natively, no snapping
    if (document.body.classList.contains('gallery-open')) return
    e.preventDefault()
    const ax = Math.abs(e.deltaX), ay = Math.abs(e.deltaY)
    // Horizontal trackpad scroll → model rotation
    if (ax > ay && ax > 3) {
      if (onHorizontalDelta) onHorizontalDelta(e.deltaX)
      return
    }
    // Filter tiny inertia residue (trackpad deceleration tail)
    if (ay < 8) return
    if (blocked()) return
    goToIndex(current + (e.deltaY > 0 ? 1 : -1))
  }, { passive: false })

  window.addEventListener('keydown', (e) => {
    if (document.body.classList.contains('gallery-open')) return
    if (blocked()) return
    if (e.key === 'ArrowDown' || e.key === 'PageDown') { e.preventDefault(); goToIndex(current + 1) }
    if (e.key === 'ArrowUp'   || e.key === 'PageUp'  ) { e.preventDefault(); goToIndex(current - 1) }
  })

  // Touch — trigger on swipe end (40px threshold)
  let touchStartY = null
  window.addEventListener('touchstart', (e) => {
    if (e.touches.length > 0) touchStartY = e.touches[0].clientY
  }, { passive: true })

  window.addEventListener('touchend', (e) => {
    if (document.body.classList.contains('gallery-open')) { touchStartY = null; return }
    if (touchStartY === null || blocked()) { touchStartY = null; return }
    const dy = touchStartY - (e.changedTouches[0]?.clientY ?? touchStartY)
    if (Math.abs(dy) > 50) goToIndex(current + (dy > 0 ? 1 : -1))
    touchStartY = null
  }, { passive: true })

  // ── Per-frame update ─────────────────────────────────────────────────────
  function update() {
    if (!tweening || snaps.length === 0) return smooth

    const t = Math.min((performance.now() - tweenStart) / tweenDuration, 1)
    smooth = tweenFrom + (tweenTo - tweenFrom) * easeInOut(t)

    if (t >= 1) {
      smooth       = tweenTo
      tweening     = false
      lastTweenEnd = performance.now()
    }
    return smooth
  }

  function setSnaps(points) {
    snaps   = points
    current = 0
    smooth  = snaps[0] ?? 0
    tweenTo = smooth
    tweening = false
  }

  function goTo(value) {
    if (!Number.isFinite(value) || snaps.length === 0) return
    let nearest = 0, nearestDist = Infinity
    snaps.forEach((s, i) => {
      const d = Math.abs(s - value)
      if (d < nearestDist) { nearestDist = d; nearest = i }
    })
    goToIndex(nearest)
  }

  function jumpTo(value) {
    if (!Number.isFinite(value) || snaps.length === 0) return
    let nearest = 0, nearestDist = Infinity
    snaps.forEach((s, i) => {
      const d = Math.abs(s - value)
      if (d < nearestDist) { nearestDist = d; nearest = i }
    })
    current      = nearest
    smooth       = snaps[nearest]
    tweenTo      = smooth
    tweening     = false
    lastTweenEnd = performance.now()
  }

  function getRaw()      { return snaps[current] ?? 0 }
  function getIndex()    { return current }
  function isTweening()  { return tweening }

  return { update, goTo, jumpTo, setSnaps, getRaw, getIndex, setHorizontalHandler, isTweening }
}
